import React, {Component} from 'react';
import ListingFeatures from './ListingFeatures';
import {
    Card,
    CardHeader,
    CardMedia
} from 'material-ui/Card'
import Avatar from 'material-ui/Avatar';
import IconButton from 'material-ui/IconButton'
import LazyLoad, { forceCheck } from 'react-lazyload';

// Needed for onTouchTap
import injectTapEventPlugin from 'react-tap-event-plugin'
injectTapEventPlugin()

import FontIcon from 'material-ui/FontIcon'
import {blue500, red500, teal500} from 'material-ui/styles/colors'

const iconStyles = {
    marginRight: 24
}

// Brand colors - Updated to match your new SCSS variables
const brandPrimary = "#ff8551";   // Warm golden-peach (soft, inviting primary)
const brandSecondary = "#f76164"; // Warm coral-red (harmonious with golden-peach)
const brandDanger = "#FF5A5A";    // Coral red (matches your new secondary warmth)
const brandSuccess = "#52C975";   // Fresh green with coral undertones
const brandInfo = "#5BB3D1";      // Deeper teal-blue (complements the warmer coral tones)
const brandDarkGrey = "#4D5356";  // From brand guidelines
const brandLightGrey = "#F8F9FA"; // Warmer light grey that complements coral

const MOBILE_BREAKPOINT = 768; // Define your mobile breakpoint
const TABLET_BREAKPOINT = 1024; // iPad and similar tablets

// Default placeholder avatar
const PLACEHOLDER_AVATAR = '/images/avatar.png';

// Detect if device is touch-enabled (iPad, tablets, phones)
const isTouchDevice = () => {
    return 'ontouchstart' in window || 
           navigator.maxTouchPoints > 0 || 
           navigator.msMaxTouchPoints > 0;
};

class Listing extends Component {
    constructor(props) {
        super(props);

        this.state = {
            ...this.props.item,
            isMobile: false, // Initialize isMobile state
            isTablet: false, // Track tablet devices
            isTouchDevice: isTouchDevice(), // Track touch capability
            avatarError: false // Track if avatar image failed to load
        };
        this.updateWindowDimensions = this.updateWindowDimensions.bind(this);
        this.handleAvatarError = this.handleAvatarError.bind(this);
    }

    handleAvatarError() {
        this.setState({ avatarError: true });
    }

    componentDidMount() {
        this.updateWindowDimensions();
        window.addEventListener('resize', this.updateWindowDimensions);
        // Force LazyLoad to check visibility after mounting
        setTimeout(() => {
            forceCheck();
        }, 100);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateWindowDimensions);
    }

    updateWindowDimensions() {
        const width = window.innerWidth;
        this.setState({ 
            isMobile: width < MOBILE_BREAKPOINT,
            isTablet: width >= MOBILE_BREAKPOINT && width <= TABLET_BREAKPOINT
        });
    }    componentWillReceiveProps(nextProps) {
        if (!_.isEqual(nextProps.item, this.state)) {
            this.setState({...nextProps.item});
        }
    }

    getListingUrl() {
        let url = this.state.routes.readMore;
        
        // Add date parameters if they exist in the filter
        if (this.props.filterStartDate || this.props.filterEndDate) {
            const urlParams = new URLSearchParams();
            if (this.props.filterStartDate) {
                urlParams.append('starts_at', this.props.filterStartDate);
            }
            if (this.props.filterEndDate) {
                urlParams.append('ends_at', this.props.filterEndDate);
            }
            url += (url.includes('?') ? '&' : '?') + urlParams.toString();
        }
        
        return url;
    }    getAvailabilityStatus() {
        // Check if this listing has specific availability state from API
        if (this.state.availability_state) {
            return this.state.availability_state;
        }
        
        // Check if this listing is unavailable for the searched dates (legacy support)
        if (this.state.is_available_for_dates === false) {
            return 'unavailable';
        }
        
        // Default to available if status is available
        return this.state.status === 'available' ? 'available' : 'unavailable';
    }

    getStatusText() {
        const status = this.getAvailabilityStatus();
        switch(status) {
            case 'available':
                return 'AVAILABLE';
            case 'pending':
                return 'PENDING';
            case 'unavailable':
            default:
                return 'UNAVAILABLE';
        }
    }

    render() {
        // Card header styles
        const cardHeaderStyle = {
            padding: '16px',
        }
        
        const cardHeaderTitleStyle = {
            fontSize: '15px',
            fontWeight: 600,
            color: brandDarkGrey, // Updated to use brand dark grey
            marginBottom: '4px'
        }
        
        const cardStyle = {
            borderRadius: '12px', boxShadow: 'none', border: '1px solid #e2e6e8',
            overflow: 'hidden', width: '100%', boxSizing: 'border-box', position: 'relative'
        };
        const getStatusBadgeBackground = () => {
            const status = this.getAvailabilityStatus();
            switch(status) {
                case 'available':
                    return '#fff0f3'; // coral-red secondary color for available
                case 'pending':
                    return '#fff2eb'; // orange for pending
                case 'unavailable':
                default:
                    return '#f1f3f4'; // warning yellow for unavailable
            }
        };
        
        const statusBadgeStyle = {
            position: 'absolute',
            top: '16px',
            right: '16px',
            zIndex: 2,
            color: this.getAvailabilityStatus() === 'available' ? '#a53550' : '#626a70',
            background: getStatusBadgeBackground(),
            padding: '3px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 600,
            display: 'inline-block'
        };
        
        // Media container style
        const mediaContainerStyle = {
            height: '220px',
            overflow: 'hidden',
            cursor: 'pointer'
        }
        
        const mediaStyle = {
            height: '100%',
            objectFit: 'cover'
        }
        


        // Always use the listing's own type by default, override with filterType if set and not 'All'
        let type = this.props.item.features?.type;
        if (this.props.filterType && this.props.filterType !== 'All') {
            type = this.props.filterType;
        }

        let placeholder = '/images/placeholders/indoor.png';
        if (type === 'Indoor Storage') {
            placeholder = '/images/placeholders/indoor.png';
        } else if (type === 'Outdoor Storage') {
            placeholder = '/images/placeholders/outdoor.png';
        } else if (type === 'Parking Space') {
            placeholder = '/images/placeholders/parking.png';
        }

        // Determine avatar URL with fallback
        const avatarUrl = this.state.avatarError || !this.state.host.small_avatar_url 
            ? PLACEHOLDER_AVATAR 
            : this.state.host.small_avatar_url;

        const cardContent = (
            <a className="listing-card-link" href={this.getListingUrl()} aria-label={this.state.title}>
            <Card
                style={cardStyle}
            >
                <div style={statusBadgeStyle}>{this.getStatusText()}</div>
                <CardHeader 
                    title={this.state.host.name} 
                    titleStyle={cardHeaderTitleStyle} 
                    avatar={
                        <Avatar 
                            src={avatarUrl}
                            onError={this.handleAvatarError}
                            style={{objectFit: 'cover'}}
                        />
                    }
                    style={cardHeaderStyle}
                />
                <CardMedia 
                    mediaStyle={{height: '100%'}}
                    style={mediaContainerStyle}
                >
                    <img 
                        src={this.state.image ? this.state.image : placeholder} 
                        alt={this.state.title}
                        loading="lazy"
                        decoding="async"
                        width="800" height="600"
                        style={mediaStyle}
                    />
                </CardMedia>

                <div className="listing-card-summary">
                    <div className="listing-card-details">
                        <h2 className="listing-card-name">{this.state.title}</h2>
                        <div className="listing-card-location">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true" focusable="false">
                                <path d="M20 10c0 6-8 11-8 11S4 16 4 10a8 8 0 1 1 16 0Z" />
                                <circle cx="12" cy="10" r="2.5" />
                            </svg>
                            <span>{this.state.publicAddress || 'Location available on enquiry'}</span>
                        </div>
                    </div>
                    <div className="listing-card-rate">
                        <span className="listing-card-amount">{this.state.formattedPrice.replace(' / week', '')}</span>
                        <span className="listing-card-period"> / week</span>
                    </div>
                </div>
                <ListingFeatures features={this.state.features || {}} rules={this.state.rules} />
            </Card>
            </a>
        );

        // Skip lazy loading on mobile and touch devices (including iPads) for better scroll performance
        if (this.state.isMobile || this.state.isTouchDevice) {
            return cardContent;
        }

        // Desktop with mouse - use optimized lazy loading
        return ( 
            <LazyLoad 
                height={380}       
                offset={1200}      
                throttle={50}      
                once              
                resize            
                scroll            
                placeholder={<div style={{height: '380px', width: '100%', background: '#f5f5f5', borderRadius: '8px'}}/>} 
            >
                {cardContent}
            </LazyLoad> 
        )
    }
}

export default Listing
